import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Combobox } from '@/components/combobox';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { fetchProjectPage } from '@/components/project-select';
import { ResponsiveTable } from '@/components/responsive-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { VotingPeriodDialog, type VotingPeriodValues } from '@/components/voting-period-dialog';
import { useVotingPeriods, useVotingResults } from '@/lib/api-hooks';
import { invalidateResource } from '@/lib/resource';
import { cn } from '@/lib/utils';
import {
  VOTING_PAGE_SIZE_OPTIONS,
  type VotingOrderKey,
  type VotingPeriod,
  type VotingStatus,
  closeVotingPeriod,
  createVotingPeriod,
  extendVotingPeriod,
  finalizeVoting,
  formatVotingDate,
} from '@/lib/voting';
import { useVotingStore } from '@/lib/voting-store';

const statusStyles: Record<VotingStatus, string> = {
  Open: 'border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  Closed: 'border-transparent bg-zinc-500/15 text-zinc-600 dark:text-zinc-400',
  Scheduled: 'border-transparent bg-blue-500/15 text-blue-700 dark:text-blue-400',
};

const statusFilters: Array<'all' | VotingStatus> = ['all', 'Open', 'Closed', 'Scheduled'];

const orderOptions: Array<{ value: VotingOrderKey; label: string }> = [
  { value: 'newest', label: 'Recently updated' },
  { value: 'closes', label: 'Closing soon' },
  { value: 'votes', label: 'Most votes' },
];

export function VotingPage() {
  const search = useVotingStore((state) => state.search);
  const status = useVotingStore((state) => state.status);
  const order = useVotingStore((state) => state.order);
  const page = useVotingStore((state) => state.page);
  const pageSize = useVotingStore((state) => state.pageSize);
  const setSearch = useVotingStore((state) => state.setSearch);
  const setStatus = useVotingStore((state) => state.setStatus);
  const setOrder = useVotingStore((state) => state.setOrder);
  const setPage = useVotingStore((state) => state.setPage);
  const setPageSize = useVotingStore((state) => state.setPageSize);

  const { data, isLoading } = useVotingPeriods({ search, status, order, page, pageSize });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [extending, setExtending] = useState<VotingPeriod | null>(null);
  const [finalizing, setFinalizing] = useState<VotingPeriod | null>(null);

  const [resultsProject, setResultsProject] = useState({ id: '', label: '' });
  const { data: resultsData } = useVotingResults(resultsProject.id);
  const results = resultsData?.results ?? [];
  const maxVotes = Math.max(1, ...results.map((result) => result.votes));

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function openCreate() {
    setExtending(null);
    setDialogOpen(true);
  }

  function openExtend(period: VotingPeriod) {
    setExtending(period);
    setDialogOpen(true);
  }

  async function handleSubmit(values: VotingPeriodValues) {
    if (extending) {
      await extendVotingPeriod(extending.id, values.closesAt);
    } else {
      await createVotingPeriod(values);
    }
    setPage(1);
    invalidateResource('voting');
  }

  async function closePeriod(period: VotingPeriod) {
    await closeVotingPeriod(period.id);
    invalidateResource('voting');
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="relative w-full sm:w-60">
            <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search projects"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-8"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-muted-foreground text-sm sm:inline">Order by</span>
            <Select value={order} onValueChange={(value) => setOrder(value as VotingOrderKey)}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {orderOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={openCreate}>
              <Plus />
              New period
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1">
          {statusFilters.map((filter) => (
            <Button
              key={filter}
              size="sm"
              variant={status === filter ? 'default' : 'ghost'}
              onClick={() => setStatus(filter)}
              className="h-8 capitalize"
            >
              {filter === 'all' ? 'All' : filter}
            </Button>
          ))}
        </div>

        <ResponsiveTable
          loading={isLoading}
          loadingLabel="Loading voting periods…"
          skeletonRows={pageSize}
          emptyLabel="No voting periods match your filters."
          rows={items}
          rowKey={(period) => period.id}
          columns={[
            {
              key: 'project',
              header: 'Project',
              title: true,
              cell: (period: VotingPeriod) => (
                <Link
                  to={`/projects/${period.projectSlug}`}
                  className="font-medium hover:underline"
                >
                  {period.project}
                </Link>
              ),
            },
            {
              key: 'opens',
              header: 'Opens',
              secondary: true,
              cell: (period: VotingPeriod) => (
                <span className="text-muted-foreground">{formatVotingDate(period.opensAt)}</span>
              ),
            },
            {
              key: 'closes',
              header: 'Closes',
              cell: (period: VotingPeriod) => (
                <span className="text-muted-foreground">{formatVotingDate(period.closesAt)}</span>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              cell: (period: VotingPeriod) => {
                const current = period.status;
                return (
                  <Badge variant="outline" className={cn(statusStyles[current])}>
                    {current}
                  </Badge>
                );
              },
            },
            {
              key: 'votes',
              header: 'Votes',
              align: 'right',
              cell: (period: VotingPeriod) => period.votes.toLocaleString(),
            },
          ]}
          actions={(period) => (
            <div className="flex items-center justify-end gap-1">
              {period.status === 'Open' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  onClick={() => setFinalizing(period)}
                >
                  Finalize
                </Button>
              )}
              {period.mode === 'manual' && period.closesAt === null ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  onClick={() => closePeriod(period)}
                >
                  Close
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  onClick={() => openExtend(period)}
                >
                  Extend
                </Button>
              )}
            </div>
          )}
        />

        {(total > 0 || isLoading) && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-muted-foreground text-sm">
              {isLoading ? 'Loading…' : `Showing ${start}–${end} of ${total}`}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Rows per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => setPageSize(Number(value))}
                >
                  <SelectTrigger size="sm" className="w-[4.5rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VOTING_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isLoading}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => (
                  <Button
                    key={number}
                    variant={number === page ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage(number)}
                    disabled={isLoading}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-3">
          <div className="grid gap-1.5">
            <CardDescription>Results</CardDescription>
            <CardTitle>Live tally</CardTitle>
          </div>
          <div className="w-48">
            <Combobox
              value={resultsProject.label}
              onChange={(option) => setResultsProject({ id: option.id, label: option.label })}
              fetchPage={fetchProjectPage}
              cacheKey="projects"
              placeholder="Search projects"
              emptyLabel="No projects found."
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {results.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {resultsProject.id ? 'No votes recorded yet.' : 'Select a project to see its tally.'}
            </p>
          ) : (
            results.map((result) => (
              <div key={result.artist} className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <Link
                    to={`/artists/${result.artistSlug}`}
                    className="font-medium hover:underline"
                  >
                    {result.artist}
                  </Link>
                  <span className="text-muted-foreground">{result.votes.toLocaleString()}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${(result.votes / maxVotes) * 100}%` }}
                  />
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {dialogOpen && (
        <VotingPeriodDialog
          open
          onOpenChange={(next) => {
            if (!next) setDialogOpen(false);
          }}
          mode={extending ? 'extend' : 'create'}
          initialValues={
            extending
              ? {
                  project: extending.project,
                  mode: extending.mode,
                  opensAt: extending.opensAt,
                  closesAt: extending.closesAt ?? '',
                }
              : undefined
          }
          onSubmit={handleSubmit}
        />
      )}

      {finalizing && (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setFinalizing(null);
          }}
          title="Finalize voting?"
          description="This closes voting, marks the top-voted proposal as the winner, and moves the project to Winner Selected."
          confirmLabel="Finalize"
          confirmVariant="default"
          onConfirm={async () => {
            await finalizeVoting(finalizing.projectSlug);
            invalidateResource('voting');
            invalidateResource('project');
            invalidateResource('proposal');
          }}
        />
      )}
    </div>
  );
}
